import { useQuery } from '@tanstack/react-query';

import { EventRsvpOption } from '@shared/types/event';

import API from '@api/API';

import { useAppContext } from '../contexts/AppContext';

export enum SocialCircleMemberRole {
  Owner = 'owner',
  Editor = 'editor',
  Member = 'member',
}

export enum SocialCircleMemberStatus {
  Pending = 'pending',
  Accepted = 'accepted',
  Rejected = 'rejected',
}

export interface SocialCircleInquiry {
  _id: string;
  title?: string;
  message: string;
  eventId?: string;
  eventDate?: string;
}

export interface SocialCircle {
  _id: string;
  name: string;
  description?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  membershipStatus: SocialCircleMemberStatus;
  membershipRole: SocialCircleMemberRole;
  acceptedMembershipCount: number;
  pendingMembershipCount: number;
  totalMembershipCount: number;
  upcomingInquiries: SocialCircleInquiry[];
}

export interface HydratedSocialCircleInquiry extends SocialCircleInquiry {
  _id: string;
  socialCircleId: string;
  conversationId: string;
  userId: string;
  circle: {
    _id: string;
    name: string;
    description?: string;
  } | null;
  event: {
    _id: string;
    title: string;
    startDate: string;
  } | null;
  responses: Record<string, { rsvpStatus: EventRsvpOption }>;
  createdAt: string;
  updatedAt: string;
}

export const useCircleInquiryQuery = (inquiryId: string) => {
  const { userIsLoggedIn } = useAppContext();

  const { isFetched, data, refetch } = useQuery({
    queryKey: useCircleInquiryQuery.queryKey(inquiryId),
    queryFn: () => useCircleInquiryQuery.queryFn(inquiryId),
    enabled: userIsLoggedIn && !!inquiryId,
  });

  return {
    inquiryIsReady: isFetched,
    inquiry: data?.inquiry,
    refetchInquiry: refetch,
  };
};
useCircleInquiryQuery.queryKey = (inquiryId: string) => ['user', 'circle-inquiry', inquiryId];
useCircleInquiryQuery.queryFn = (inquiryId: string) =>
  API.get<{ inquiry: HydratedSocialCircleInquiry }>(`/v1/user/circles/inquiries/${inquiryId}`);

export const useUserSocialCirclesQuery = () => {
  const { userIsLoggedIn } = useAppContext();

  const { isFetched, data, refetch } = useQuery({
    queryKey: useUserSocialCirclesQuery.queryKey(),
    queryFn: useUserSocialCirclesQuery.queryFn,
    enabled: userIsLoggedIn,
  });

  return {
    socialCirclesAreReady: isFetched,
    socialCircles: data?.circles ?? [],
    refetchSocialCircles: refetch,
  };
};
useUserSocialCirclesQuery.queryKey = () => ['user', 'social-circles'];
useUserSocialCirclesQuery.queryFn = () => API.get<{ circles: SocialCircle[] }>(`/v1/user/circles`);
